/**
 * The two requests every authenticated boot needs before anything can render
 * — the allauth session (`getAuth`) and the org list — started at parse time
 * from prefetch.ts, before React mounts, so they overlap with script evaluation
 * instead of queuing behind it.
 *
 * AuthContextProvider and TeamProvider ADOPT these promises on their first
 * load (`takeBootAuthRequest` / `takeBootOrgsRequest`) and fall back to
 * issuing their own request when none is pending — so the providers behave
 * identically whether or not the prefetch script ran (client-side org
 * switches, tests, booking hosts).
 *
 * Each promise is handed out once. A second caller gets null and fetches for
 * itself, which keeps retry semantics (auth retries, org reload after an org
 * switch) exactly as they were.
 *
 * Each request carries an explicit timeout: if the slot is still occupied when
 * the timer fires, it is cleared so a late-mounting provider falls back to
 * issuing its own fresh request rather than waiting on a potentially-stuck
 * promise. The in-flight network request is not aborted — it keeps running in
 * the background in case it eventually resolves.
 *
 * `ensureDashboardPrefetch` is re-exported here so every boot-request consumer
 * imports from one canonical file rather than reaching into the prefetch
 * registry internals.
 */

import { OrgsApi, ResponseError, type Team } from 'api-client';

import { getApiConfiguration } from '@/api/utils';
import { getAuth, getConfig } from '@/lib/allauth';
import { mark } from '@/lib/boot/bootMetrics';
import { logger } from '@/lib/logger';
import { parseOrgRoute } from '@/lib/prefetch/parseOrgRoute';

export { ensureDashboardPrefetch } from '@/lib/prefetch/entries/dashboard';

/**
 * How long to keep a boot-request slot occupied before clearing it.
 * A provider that mounts after this deadline finds the slot empty and issues
 * its own fresh request — degrading gracefully rather than hanging.
 */
const BOOT_REQUEST_TIMEOUT_MS = 8_000;

let bootAuth: Promise<unknown> | null = null;
let bootConfig: Promise<unknown> | null = null;
let bootOrgs: Promise<Team[]> | null = null;
let started = false;

/**
 * Fetch the user's org list and guarantee the org named in the URL is in it.
 *
 * `teamsList()` returns only the first page — 20 orgs, newest first. A user
 * who belongs to more orgs than that can deep-link to an older org and find
 * it missing from the list; `selectInitialCurrentOrg` then silently falls
 * back to a different org while the URL still names the requested one. (Seen
 * on staging, where the shared e2e user belongs to thousands of orgs and the
 * reused test org is the oldest of them.) When the URL org isn't on the first
 * page, fetch it by id. A 403/404 means the user genuinely has no access —
 * leave it out and let TeamGuard's existing "not in list" redirect apply.
 */
export async function fetchAvailableOrgs(api: OrgsApi): Promise<Team[]> {
  const response = await api.teamsList();
  const teams = response.results || [];
  const route = parseOrgRoute(window.location.pathname);
  if (!route || teams.some(team => team.id === route.orgId)) {
    return teams;
  }
  try {
    const urlOrg = await api.teamsRetrieve({ id: route.orgId });
    logger.debug('OrgContext: URL org missing from first page, fetched by id', {
      orgId: route.orgId,
      firstPageCount: teams.length,
    });
    return [urlOrg, ...teams];
  } catch (err: unknown) {
    const status = err instanceof ResponseError ? err.response.status : null;
    if (status === 403 || status === 404) {
      logger.debug('OrgContext: URL org not accessible to this user', {
        orgId: route.orgId,
        status,
      });
    } else {
      logger.warn(
        'OrgContext: failed to fetch URL org by id, falling back to first page',
        { orgId: route.orgId, status, component: 'org-context' }
      );
    }
    return teams;
  }
}

/**
 * Kick off the auth session, allauth config, and org-list requests. Idempotent.
 * Callers must have configured the allauth client (`initAllauth`) first.
 *
 * Each slot is cleared after `BOOT_REQUEST_TIMEOUT_MS` so a provider that
 * mounts very late (e.g. extremely slow JS parse on low-end hardware) falls
 * back to issuing its own request rather than adopting a promise that may
 * never have resolved.
 */
export function startBootRequests(): void {
  if (started) {
    return;
  }
  started = true;
  mark('boot:requests-start');

  const auth = getAuth();
  const config = getConfig();
  const orgs = fetchAvailableOrgs(new OrgsApi(getApiConfiguration()));

  bootAuth = auth;
  bootConfig = config;
  bootOrgs = orgs;

  // Safety valves: after BOOT_REQUEST_TIMEOUT_MS, clear each slot so a
  // late-mounting provider finds null and issues its own fresh request.
  // The in-flight network requests are not aborted — they continue and may
  // still resolve if a consumer already adopted them.
  const authTimeout = setTimeout(() => {
    if (bootAuth === auth) {
      bootAuth = null;
    }
  }, BOOT_REQUEST_TIMEOUT_MS);
  const configTimeout = setTimeout(() => {
    if (bootConfig === config) {
      bootConfig = null;
    }
  }, BOOT_REQUEST_TIMEOUT_MS);
  const orgsTimeout = setTimeout(() => {
    if (bootOrgs === orgs) {
      bootOrgs = null;
    }
  }, BOOT_REQUEST_TIMEOUT_MS);

  // Clear timers eagerly when requests settle (happy path cleans up).
  auth.then(
    () => clearTimeout(authTimeout),
    () => clearTimeout(authTimeout)
  );
  config.then(
    () => clearTimeout(configTimeout),
    () => clearTimeout(configTimeout)
  );
  orgs.then(
    () => clearTimeout(orgsTimeout),
    () => clearTimeout(orgsTimeout)
  );

  // Consumers attach their own handlers when they adopt the promise; these
  // stop a rejection from surfacing as "unhandled" in the window before then.
  bootAuth.catch(() => {});
  bootConfig.catch(() => {});
  bootOrgs.catch(() => {});
}

/** The pending auth-session request, handed out once; null when none. */
export function takeBootAuthRequest(): Promise<unknown> | null {
  const pending = bootAuth;
  bootAuth = null;
  return pending;
}

/** The pending allauth-config request, handed out once; null when none. */
export function takeBootConfigRequest(): Promise<unknown> | null {
  const pending = bootConfig;
  bootConfig = null;
  return pending;
}

/** The pending org-list request, handed out once; null when none. */
export function takeBootOrgsRequest(): Promise<Team[]> | null {
  const pending = bootOrgs;
  bootOrgs = null;
  return pending;
}

/**
 * Test-only — forget any started requests and reset state so the next
 * `startBootRequests()` call behaves like a fresh page load.
 */
export function resetBootRequestsForTest(): void {
  bootAuth = null;
  bootConfig = null;
  bootOrgs = null;
  started = false;
}
